import React from 'react';
import { Card, Text } from '@mantine/core';
import { MetricConfig, MetricData } from '../../types/metrics';
import LineChart from './charts/LineChart';
import BarChart from './charts/BarChart';
import PieChart from './charts/PieChart';
import StackedBarChart from './charts/StackedBarChart';
import { MetricTitle } from '../ui/MetricTitle';
import { getChartSubtitle, getPieChartValueLabel } from '../../utils/chartSubtitles';

interface ChartRendererProps {
  config: MetricConfig;
  data: MetricData | null;
  loading: boolean;
  error?: string | null;
  startDate?: string;
  endDate?: string;
}

const ChartRenderer: React.FC<ChartRendererProps> = ({
  config,
  data,
  loading,
  error,
  startDate,
  endDate,
}) => {
  const renderChart = () => {
    // Para gráficos, los datos vienen directamente del hook
    if (!data) {
      console.log(`❌ ChartRenderer ${config.id} - No data available`);
      return <div>Sin datos disponibles</div>;
    }

    // Si los datos están en data.data (respuesta del backend), usarlos
    // Si no, usar data directamente
    const chartData = Array.isArray(data) ? data : (data.data || data);

    if (!chartData || !Array.isArray(chartData) || chartData.length === 0) {
      console.log(`❌ ChartRenderer ${config.id} - No chart data available`);
      return <div>Sin datos disponibles</div>;
    }

    console.log(`📊 ChartRenderer ${config.id} - Raw data:`, chartData);

    // Validación adicional: asegurar que todos los valores sean números válidos
    const validatedData = chartData.filter(item => {
      if (!item || typeof item !== 'object') {
        console.warn(`⚠️ Item inválido filtrado en ChartRenderer ${config.id}:`, item);
        return false;
      }
      
      // Verificar que tenga al menos una propiedad de valor válida
      const hasValidValue = Object.values(item).some(value => {
        if (typeof value === 'string' && (value === 'name' || value === 'date' || value === 'reason')) {
          return true; // Permitir propiedades de texto
        }
        const numValue = typeof value === 'number' ? value : parseFloat(String(value));
        return !isNaN(numValue) && isFinite(numValue) && numValue >= 0;
      });
      
      if (!hasValidValue) {
        console.warn(`⚠️ Item sin valores válidos filtrado en ChartRenderer ${config.id}:`, item);
      }
      
      return hasValidValue;
    });

    if (validatedData.length === 0) {
      console.log(`❌ ChartRenderer ${config.id} - No valid data after filtering`);
      return <div>Sin datos válidos disponibles</div>;
    }

    // Validación específica para gráficos de CyD
    if (config.id === 'cyd-by-reason' || config.id === 'cyd-trend') {
      const finalData = validatedData.map(item => {
        if (config.id === 'cyd-by-reason') {
          return {
            ...item,
            cambiar: Math.max(0, Math.round((item as any).cambiar * 100) / 100), // Permitir 0 en porcentajes
            devolver: Math.max(0, Math.round((item as any).devolver * 100) / 100), // Permitir 0 en porcentajes
            total: Math.max(0, Math.round((item as any).total * 100) / 100) // Permitir 0 en porcentajes
          };
        } else if (config.id === 'cyd-trend') {
          return {
            ...item,
            value: Math.max(0.01, (item as any).value || 0)
          };
        }
        return item;
      });
      
      console.log(`📊 ChartRenderer ${config.id} - Final validated data:`, finalData);
      
      const chartProps = {
        data: finalData,
        title: config.title,
        loading: loading
      };

      switch (config.chartType) {
        case 'line':
          return <LineChart {...chartProps} />;
        case 'bar':
          return <BarChart {...chartProps} />;
        case 'pie':
          return <PieChart {...chartProps} />;
        case 'stacked-bar':
          return <StackedBarChart {...chartProps} />;
        default:
          return <div>Tipo de gráfico no soportado: {config.chartType}</div>;
      }
    }

    const pieValueLabel = getPieChartValueLabel(config.id);

    console.log(`📊 ChartRenderer ${config.id} - Final data:`, validatedData);

    const chartProps = {
      data: validatedData,
      title: config.title,
      loading: loading,
      valueLabel: config.chartType === 'pie' ? pieValueLabel : undefined,
    };

    switch (config.chartType) {
      case 'line':
        return <LineChart {...chartProps} />;
      case 'bar':
        return <BarChart {...chartProps} />;
      case 'pie':
        return <PieChart {...chartProps} />;
      case 'stacked-bar':
        return <StackedBarChart {...chartProps} />;
      default:
        return <div>Tipo de gráfico no soportado: {config.chartType}</div>;
    }
  };

  const subtitle = getChartSubtitle(config.id, { startDate, endDate });

  const renderHeader = () => (
    <MetricTitle title={config.title} metricId={config.id} subtitle={subtitle} mb="md" />
  );

  if (loading) {
    return (
      <Card className="metric-panel" shadow="sm" padding="md" radius="md" withBorder>
        {renderHeader()}
        <Text>Cargando gráfico...</Text>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="metric-panel" shadow="sm" padding="md" radius="md" withBorder>
        {renderHeader()}
        <Text c="negative">Error: {error}</Text>
      </Card>
    );
  }

  return (
    <Card className="metric-panel" shadow="sm" padding="md" radius="md" withBorder>
      {renderHeader()}
      {renderChart()}
    </Card>
  );
};

export default ChartRenderer;
